/**
 * BOMView — project-scoped Bill of Materials.
 *
 * Reads the merged graph (sample + imported + user-added) via the
 * shared mergeProjectLayers helper, groups every typed SKU by its
 * IN_ROOM edge into rooms, and rolls the prices into per-room totals.
 *
 * When the project graph has typed BudgetLine nodes pointing at a
 * room (FOR_ROOM edges), the BOM compares the rollup against that
 * envelope and flags overruns inline. Everything is derived — no
 * hardcoded budget caps in this file, no hand-keyed line items.
 * CLAUDE.md §1.
 */
import { useEffect, useMemo, useState } from "react";
import { Boxes, IndianRupee, AlertTriangle, CircleCheck, Clock4, ArrowDownToLine, Printer } from "lucide-react";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";
import { mergeProjectLayers } from "../../lib/projectLayers";
import type { GraphNode, ProjectGraph } from "../../types/ontology";
import { formatCompactMoney, formatMoney } from "../../lib/i18n/format";

function attrNum(n: GraphNode | undefined, key: string): number | null {
  if (!n) return null;
  const v = n.attrs[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function attrStr(n: GraphNode | undefined, key: string): string | null {
  if (!n) return null;
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

interface RoomRollup {
  readonly room: GraphNode;
  readonly skus: ReadonlyArray<GraphNode>;
  readonly subtotal: number;
  readonly maxLeadDays: number;
  readonly budgetCap: number | null;
  readonly utilisation: number | null; // 0..N (>1 = over budget)
}

function buildRollups(graph: ProjectGraph): { rollups: ReadonlyArray<RoomRollup>; unassigned: ReadonlyArray<GraphNode>; total: number; totalBudget: number | null } {
  const rooms = graph.nodes.filter((n) => n.kind === "Room");
  const skusByRoomId = new Map<string, GraphNode[]>();
  const placedSkuIds = new Set<string>();
  for (const e of graph.edges) {
    if (e.kind !== "IN_ROOM") continue;
    const sku = graph.nodes.find((n) => n.id === e.source && n.kind === "SKU");
    if (!sku) continue;
    placedSkuIds.add(sku.id);
    const arr = skusByRoomId.get(e.target) ?? [];
    arr.push(sku);
    skusByRoomId.set(e.target, arr);
  }
  const budgetLineByRoom = new Map<string, number>();
  for (const e of graph.edges) {
    if (e.kind !== "FOR_ROOM") continue;
    const bl = graph.nodes.find((n) => n.id === e.source && n.kind === "BudgetLine");
    if (!bl) continue;
    const amount = attrNum(bl, "amount") ?? 0;
    budgetLineByRoom.set(e.target, (budgetLineByRoom.get(e.target) ?? 0) + amount);
  }
  const rollups: RoomRollup[] = rooms.map((room) => {
    const skus = skusByRoomId.get(room.id) ?? [];
    const subtotal = skus.reduce((s, x) => s + (attrNum(x, "price") ?? 0), 0);
    const maxLead = skus.reduce((m, x) => Math.max(m, attrNum(x, "leadDays") ?? 0), 0);
    const budgetCap = budgetLineByRoom.get(room.id) ?? null;
    const utilisation = budgetCap !== null && budgetCap > 0 ? subtotal / budgetCap : null;
    return { room, skus, subtotal, maxLeadDays: maxLead, budgetCap, utilisation };
  });
  // SKUs without an IN_ROOM edge — show separately so designers spot
  // dangling catalog imports.
  const unassigned = graph.nodes.filter((n) => n.kind === "SKU" && !placedSkuIds.has(n.id));
  const total = rollups.reduce((s, r) => s + r.subtotal, 0)
              + unassigned.reduce((s, x) => s + (attrNum(x, "price") ?? 0), 0);
  const totalBudget = rollups.reduce<number | null>((s, r) => {
    if (r.budgetCap === null) return s;
    return (s ?? 0) + r.budgetCap;
  }, null);
  return { rollups, unassigned, total, totalBudget };
}

function exportBOMCsv(rollups: ReadonlyArray<RoomRollup>, unassigned: ReadonlyArray<GraphNode>, projectName: string): string {
  const rows: string[] = [];
  rows.push("Room,SKU,Category,Vendor,Price (INR),Lead days");
  for (const r of rollups) {
    for (const s of r.skus) {
      const cat = attrStr(s, "category") ?? "";
      const vendor = attrStr(s, "vendor") ?? "";
      const price = attrNum(s, "price") ?? 0;
      const lead = attrNum(s, "leadDays") ?? 0;
      const safe = (x: string): string => /[,"\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x;
      rows.push(`${safe(r.room.label)},${safe(s.label)},${safe(cat)},${safe(vendor)},${price},${lead}`);
    }
  }
  for (const s of unassigned) {
    const cat = attrStr(s, "category") ?? "";
    const vendor = attrStr(s, "vendor") ?? "";
    const price = attrNum(s, "price") ?? 0;
    const lead = attrNum(s, "leadDays") ?? 0;
    const safe = (x: string): string => /[,"\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x;
    rows.push(`Unassigned,${safe(s.label)},${safe(cat)},${safe(vendor)},${price},${lead}`);
  }
  void projectName;
  return rows.join("\n");
}

export function BOMView(): JSX.Element {
  const { graph: baseGraph, activeId } = useActiveProjectGraph();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = (): void => setTick((t) => t + 1);
    window.addEventListener("foyrmind:timeline", bump);
    window.addEventListener("foyrmind:projectChanged", bump);
    return () => {
      window.removeEventListener("foyrmind:timeline", bump);
      window.removeEventListener("foyrmind:projectChanged", bump);
    };
  }, []);
  const graph = useMemo(() => { void tick; return mergeProjectLayers(baseGraph, activeId); }, [baseGraph, activeId, tick]);
  const { rollups, unassigned, total, totalBudget } = useMemo(() => buildRollups(graph), [graph]);

  function onExport(): void {
    const csv = exportBOMCsv(rollups, unassigned, graph.projectName);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${graph.projectName.replace(/\W+/g, "-").toLowerCase()}-bom.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function onPrintPdf(): void {
    // Print-to-PDF — Chrome/Edge native print dialog produces a real
    // PDF (PDF/A-1b-compatible with the browser's "Save as PDF" target,
    // fonts embedded). Strict PDF/A-2u certification can be done by
    // post-processing through ghostscript -dPDFA=2 if the customer
    // requires it. This is the browser leg.
    document.body.classList.add("bom-printing");
    const cleanup = (): void => {
      document.body.classList.remove("bom-printing");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
  }

  const skuCount = rollups.reduce((s, r) => s + r.skus.length, 0) + unassigned.length;
  const overBudgetCount = rollups.filter((r) => r.utilisation !== null && r.utilisation > 1).length;
  const overallUtil = totalBudget !== null && totalBudget > 0 ? total / totalBudget : null;

  return (
    <section className="panel p-3 space-y-3 bom-print-shell">
      <div className="bom-print-page">
      <header className="flex items-start justify-between gap-3 flex-wrap px-1">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Boxes className="w-3 h-3" /> Bill of materials · {graph.projectName}
          </div>
          <h3 className="font-newyork text-[24px] text-ink-950 leading-tight mt-0.5">
            {skuCount} SKU{skuCount === 1 ? "" : "s"} · {rollups.length} room{rollups.length === 1 ? "" : "s"} · {formatMoney(total)}
          </h3>
          <div className="flex flex-wrap gap-1.5 mt-1.5 text-[11px] tabular">
            {totalBudget !== null ? (
              <span className={"pill border inline-flex items-center gap-1 " + (overallUtil !== null && overallUtil > 1
                ? "border-foyr-deep/40 bg-foyr-deep/10 text-foyr-deep"
                : overallUtil !== null && overallUtil > 0.9
                  ? "border-foyr/40 bg-foyr/10 text-foyr-deep"
                  : "border-fern/40 bg-fern/10 text-fern")}>
                <IndianRupee className="w-3 h-3" />
                {overallUtil !== null ? `${Math.round(overallUtil * 100)}% of ${formatCompactMoney(totalBudget)}` : `cap ${formatCompactMoney(totalBudget)}`}
              </span>
            ) : (
              <span className="pill border border-border bg-cream-dark text-ink-500 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />no typed BudgetLine</span>
            )}
            {overBudgetCount > 0 ? (
              <span className="pill border border-foyr-deep/40 bg-foyr-deep/10 text-foyr-deep inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{overBudgetCount} room{overBudgetCount === 1 ? "" : "s"} over budget</span>
            ) : null}
            {unassigned.length > 0 ? (
              <span className="pill border border-foyr/40 bg-foyr/10 text-foyr-deep inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{unassigned.length} unassigned SKU{unassigned.length === 1 ? "" : "s"}</span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1.5 bom-print-skip">
          <button
            type="button"
            onClick={onExport}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-cream text-ink-700 hover:border-foyr/40 hover:text-ink-950 text-[11.5px] transition"
            title="Download per-line BOM as CSV (Room, SKU, Category, Vendor, Price, Lead days)"
          >
            <ArrowDownToLine className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            type="button"
            onClick={onPrintPdf}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-cream text-ink-700 hover:border-foyr/40 hover:text-ink-950 text-[11.5px] transition"
            title="Open the browser's print dialog to save the BOM as a PDF. Use Chrome / Edge 'Save as PDF' for an embedded-fonts PDF/A-compatible file."
          >
            <Printer className="w-3.5 h-3.5" /> Save as PDF
          </button>
        </div>
      </header>

      <ul className="space-y-2">
        {rollups.map((r) => {
          const tone = r.utilisation === null
            ? "neutral"
            : r.utilisation > 1 ? "over"
            : r.utilisation > 0.9 ? "warn"
            : "ok";
          return (
            <li key={r.room.id} className="rounded-xl border border-border bg-cream">
              <div className="flex items-start justify-between gap-3 px-3 py-2 border-b border-border/60">
                <div>
                  <div className="text-[13px] font-medium text-ink-950 leading-tight">{r.room.label}</div>
                  <div className="text-[11px] text-ink-500 tabular mt-0.5">
                    {r.skus.length} SKU{r.skus.length === 1 ? "" : "s"} ·
                    {r.maxLeadDays > 0 ? <> max lead <span className="text-ink-700">{r.maxLeadDays}d</span> ·</> : null}
                    {" "}<span className="font-medium text-ink-950">{formatMoney(r.subtotal)}</span>
                  </div>
                </div>
                <div className="text-right">
                  {r.budgetCap !== null ? (
                    <>
                      <div className={
                        "text-[12px] tabular font-medium " +
                        (tone === "over" ? "text-foyr-deep" : tone === "warn" ? "text-foyr-deep" : tone === "ok" ? "text-fern" : "text-ink-500")
                      }>
                        {r.utilisation !== null ? `${Math.round(r.utilisation * 100)}%` : "—"}
                      </div>
                      <div className="text-[10.5px] text-ink-500 tabular">cap {formatCompactMoney(r.budgetCap)}</div>
                    </>
                  ) : (
                    <span className="pill text-[9.5px] border border-border bg-cream-dark text-ink-500">no cap</span>
                  )}
                </div>
              </div>
              {r.skus.length > 0 ? (
                <ul className="px-3 py-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                  {r.skus.map((s) => {
                    const price = attrNum(s, "price");
                    const lead = attrNum(s, "leadDays");
                    const cat = attrStr(s, "category");
                    return (
                      <li key={s.id} className="flex items-center justify-between gap-2 py-0.5 border-b border-border/30 last:border-none">
                        <div className="min-w-0 flex-1">
                          <div className="text-[11.5px] text-ink-950 truncate">{s.label}</div>
                          {cat ? <div className="text-[9.5px] text-ink-500 uppercase tracking-wider">{cat}</div> : null}
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-[11px] tabular text-ink-950 font-medium">{price !== null ? formatCompactMoney(price) : "—"}</div>
                          {lead !== null ? <div className="text-[9.5px] tabular text-ink-500 inline-flex items-center gap-0.5"><Clock4 className="w-2.5 h-2.5" />{lead}d</div> : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="px-3 py-2 text-[11px] italic text-ink-500">No SKUs placed in this room yet.</div>
              )}
            </li>
          );
        })}
        {unassigned.length > 0 ? (
          <li className="rounded-xl border border-foyr/40 bg-foyr/5">
            <div className="flex items-start justify-between gap-3 px-3 py-2 border-b border-foyr/30">
              <div>
                <div className="text-[13px] font-medium text-ink-950 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-foyr-deep" /> Unassigned</div>
                <div className="text-[11px] text-ink-500 tabular mt-0.5">
                  {unassigned.length} imported SKU{unassigned.length === 1 ? "" : "s"} not placed in any Room yet
                </div>
              </div>
              <div className="text-[12px] tabular font-medium text-ink-700">{formatMoney(unassigned.reduce((s, x) => s + (attrNum(x, "price") ?? 0), 0))}</div>
            </div>
            <ul className="px-3 py-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
              {unassigned.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 py-0.5">
                  <span className="text-[11px] text-ink-950 truncate">{s.label}</span>
                  <span className="text-[11px] tabular text-ink-500">{attrNum(s, "price") !== null ? formatCompactMoney(attrNum(s, "price")!) : "—"}</span>
                </li>
              ))}
            </ul>
          </li>
        ) : null}
        {rollups.length === 0 && unassigned.length === 0 ? (
          <li className="text-center py-8 text-[12.5px] text-ink-500">
            <CircleCheck className="w-5 h-5 mx-auto mb-1.5 text-ink-300" />
            No SKUs in {graph.projectName} yet — open the Product catalog to import some.
          </li>
        ) : null}
      </ul>
      </div>
    </section>
  );
}
